import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, formatMoney } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function TradeMini({ label, trade, currency, tone }) {
  if (!trade) return (
    <div className="border border-[#2E2A25] p-3 rounded-sm">
      <p className="font-mono text-[9px] uppercase tracking-widest text-[#A39A8E]">{label}</p>
      <p className="text-sm text-[#A39A8E] mt-1 font-mono">—</p>
    </div>
  );
  const color = tone === "profit" ? "text-[#7A9E80]" : tone === "loss" ? "text-[#B56258]" : "text-[#EBE5D9]";
  return (
    <div className="border border-[#2E2A25] p-3 rounded-sm">
      <p className="font-mono text-[9px] uppercase tracking-widest text-[#A39A8E]">{label}</p>
      <p className={`font-mono text-base ${color} mt-1`}>{formatMoney(trade.pnl, currency)}</p>
      <p className="font-mono text-[10px] text-[#A39A8E] mt-1">{trade.pair} · {trade.date}</p>
    </div>
  );
}

export default function LeaderboardPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      const { data } = await api.get("/leaderboard");
      setRows(data.traders);
      setLoading(false);
    })();
  }, []);
  return (
    <Layout>
      <div className="mb-8 fade-in">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#CFA66A] mb-2">— Top 100</p>
        <h1 className="font-head text-4xl sm:text-5xl font-bold text-[#EBE5D9]">Leaderboard</h1>
        <p className="text-[#A39A8E] mt-3 max-w-2xl">
          Public traders ranked by total P&amp;L. See their biggest wins, smallest wins, worst losses,
          and smallest losses. Click a name to view their full profile.
        </p>
      </div>
      {loading ? (
        <p className="text-center text-[#A39A8E] py-16 font-mono text-sm">Loading traders…</p>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-[#2E2A25] p-10 text-center rounded-sm">
          <p className="text-[#A39A8E]">No public traders with trades yet. Be the first!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r, i) => (
            <div key={r.trader.handle} data-testid={`leaderboard-row-${i}`} className="border border-[#2E2A25] bg-[#1A1815] p-5 rounded-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-4">
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-2xl text-[#CFA66A] w-10">#{i + 1}</span>
                  <div>
                    <Link to={`/u/${r.trader.handle}`} data-testid={`profile-link-${r.trader.handle}`} className="font-head text-xl text-[#EBE5D9] hover:text-[#CFA66A] transition-colors">
                      {r.trader.name}
                    </Link>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-[#A39A8E]">@{r.trader.handle} · {r.trade_count} trades · {r.win_rate}% win</p>
                  </div>
                </div>
                <p className={`font-mono text-2xl ${r.total_pnl >= 0 ? "text-[#7A9E80]" : "text-[#B56258]"}`}>
                  {formatMoney(r.total_pnl, user?.currency)}
                </p>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-4">
                <TradeMini label="Highest win" trade={r.highest_win} currency={user?.currency} tone="profit" />
                <TradeMini label="Smallest win" trade={r.lowest_win} currency={user?.currency} tone="profit" />
                <TradeMini label="Smallest loss" trade={r.smallest_loss} currency={user?.currency} tone="loss" />
                <TradeMini label="Worst loss" trade={r.worst_loss} currency={user?.currency} tone="loss" />
              </div>
              <div className="mt-3">
                <Link to={`/u/${r.trader.handle}`} className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#CFA66A] hover:text-[#EBE5D9]">
                  View profile →
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
